'use client';

import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { mapStructuredContentToMetadata, useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { BESTIE_GREETING_PROMPT } from '@hooks/features/bestieGreetingPrompt';
import { useProactiveGreeting } from '@hooks/features/useProactiveGreeting';
import { invokeChatWithSkills } from '@services/agent/chatInvoke';

interface BestieChatStateValue {
  /** Bestie chat stream — shared so it survives across the views that render the chat. */
  stream: ReturnType<typeof useStreamStateV2>['stream'];
  /** Submit a chat message to the Bestie stream. */
  submitChat: (text: string) => Promise<void>;
  /** Flag that the next chat view should open its input ready to type (e.g. when the
   *  user picks "coaching" from the prompt-bar menu). Lives here because ChatBranch
   *  remounts across the search↔chat mode switch, so a window event would race. */
  requestChatFocus: () => void;
  /** Read-and-clear the chat-focus request. Returns true once per request. */
  consumeChatFocusRequest: () => boolean;
  /** True while Bestie's opening message is in flight. Consumers show the chat view instead of
   *  the canned landing. */
  isGreetingPending: boolean;
  /** Restores this thread's past messages into the stream, above the greeting. One-shot: it
   *  no-ops once loaded or while a load is in flight. */
  loadHistory: () => Promise<void>;
  /** True while `loadHistory` is in flight. */
  isHistoryLoading: boolean;
  /** True once history has been restored for this mount. */
  hasHistoryLoaded: boolean;
}

const BestieChatStateContext = createContext<BestieChatStateValue | undefined>(undefined);

/**
 * @description Holds the shared live state for the Bestie candidate chat — the chat stream and
 * its submit handler. Mounted by the Bestie chat page so the stream + submit are available to
 * the prompt bar and the streamed-messages view.
 */
export const BestieChatStateProvider = ({ children, locale }: {
  children: ReactNode;
  locale: Locale;
}) => {
  const { stream, startStream, initializeWithHistory, seedMessage } = useStreamStateV2(null);
  const anonSessionId = useAnonSessionId();

  // The thread id — not an `isAuthenticated` prop — is authoritative for which opener path runs,
  // because the thread id is exactly what goes into the upstream request (`default:<userId>` for
  // `chat/invoke`, a cookie uuid for the guest `chat/stream` priming turn). An SSR-computed
  // `isAuthenticated` prop can disagree with it: `AuthModal` signs in with `signIn(..., {
  // redirect: false })` then `await update()` — no page reload — so `anonSessionId` flips to
  // `default:<userId>` immediately (`useAnonSessionId` reacts to the client session), while an
  // SSR prop would still say `false` until the next navigation. That modal is reachable from this
  // very page (job cards render with `jobApplyWithAuthModal`), so the mismatch is not theoretical:
  // a guest could sign in in-place and get the guest branch's priming turn written into their new,
  // signed-in thread. Branching on the thread id keeps the opener's auth check and the actual
  // request body always in agreement.
  const isAuthenticatedThread = anonSessionId?.startsWith('default:') ?? false;

  const streamHeaders = useMemo(() => ({
    'x-anon-session-id': anonSessionId ?? '',
  }), [anonSessionId]);

  const submitChat = useCallback(async (text: string) => {
    const message = { id: uuidv4(), content: text, type: 'human' as const };
    await startStream(message, '', {
      headers: streamHeaders,
      configurations: { user_locale: locale || 'ro' },
    });
  }, [startStream, streamHeaders, locale]);

  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [hasHistoryLoaded, setHasHistoryLoaded] = useState(false);

  // `anonSessionId` can change under a mounted provider — the guest cookie uuid resolves to
  // `default:<userId>` once a session settles, or the visitor signs in through a modal without
  // leaving the page. `useProactiveGreeting` is keyed by thread id, so it greets the new thread,
  // but `hasHistoryLoaded` was only ever set for the old one. Reset it so the button comes back
  // for the thread whose history was never loaded.
  useEffect(() => {
    setHasHistoryLoaded(false);
  }, [anonSessionId]);

  const loadHistory = useCallback(async () => {
    if (isHistoryLoading || hasHistoryLoaded) return;
    setIsHistoryLoading(true);
    try {
      const history = await initializeWithHistory();
      // `fetched === false` means the fetch was skipped because there was no thread id yet —
      // nothing was restored, so leave the button in place rather than marking this done.
      if (history.fetched !== false) setHasHistoryLoaded(true);
    } catch {
      // Deliberately silent, no toast: the button stays visible so the visitor can retry.
    } finally {
      setIsHistoryLoading(false);
    }
  }, [initializeWithHistory, isHistoryLoading, hasHistoryLoaded]);

  // Signed-in visitors get their opener from the agent's `home-greeting` skill, which searches
  // jobs from their profile — its reply carries the job payload we seed into the transcript.
  // Guests have no profile to search from, so they keep the hidden priming turn through the
  // stream. Both are one call per entry; the caller guards that.
  const startOpener = useCallback(async () => {
    if (!anonSessionId) return;

    if (!isAuthenticatedThread) {
      await submitChat(BESTIE_GREETING_PROMPT);
      return;
    }

    const result = await invokeChatWithSkills({
      threadId: anonSessionId,
      skills: ['home-greeting'],
    });

    // `content` is typed as required on `ChatInvokeResponse`, but that's only an assertion on
    // `response.json()` — an agent reply that omits or nulls it (while still carrying jobs in
    // `structured_content`) would otherwise throw on `.trim()` below and discard a good payload.
    const content = result.content ?? '';
    const metadata = mapStructuredContentToMetadata(result.structured_content);

    // Mirrors `messageHasResults` in `BestieMessagesList.tsx`: a seeded message only renders
    // something if it has text or at least one job/resume result. `mapStructuredContentToMetadata`
    // returns a truthy object even for `{ type: 'job_search', items: [] }` — a profile that
    // matched no jobs — so checking metadata's truthiness alone (as this guard used to) let a
    // zero-item payload through. `ChatMessageComponent` gates its whole bubble on non-empty
    // content, and job cards render outside that gate, so the result was a message with neither
    // a bubble nor cards that still counts as a visible message to `JobListContent`'s
    // `visibleMessageCount`, which suppresses `CoachingLanding` — the exact blank-page failure
    // the priming turn's hidden-message handling was written to avoid. Skip the seed so the
    // canned landing shows instead; text-only and payload-with-items replies both still seed
    // normally.
    const hasRenderableResults = (
      (metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
      || (metadata?.customData?.jobRecommendationResults?.items?.length ?? 0) > 0
      || (metadata?.customData?.resumeSearchResults?.items?.length ?? 0) > 0
    );
    if (content.trim() === '' && !hasRenderableResults) return;

    seedMessage({
      // Its own id namespace: `history-N` ids belong to restored history, and reusing one
      // would let a later history load overwrite the opener.
      id: `opener-${anonSessionId}`,
      type: 'ai',
      content,
      response_metadata: metadata,
    });
  }, [anonSessionId, isAuthenticatedThread, submitChat, seedMessage]);

  const { isGreetingPending } = useProactiveGreeting({
    threadId: anonSessionId,
    startOpener,
  });

  // One-shot intent so a chat-view mount can auto-open its input. A ref (not state)
  // because it's consumed by an effect on the next mount, not rendered.
  const chatFocusRequestRef = useRef(false);
  const requestChatFocus = useCallback(() => { chatFocusRequestRef.current = true; }, []);
  const consumeChatFocusRequest = useCallback(() => {
    const requested = chatFocusRequestRef.current;
    chatFocusRequestRef.current = false;
    return requested;
  }, []);

  const value = useMemo(
    () => ({
      stream,
      submitChat,
      requestChatFocus,
      consumeChatFocusRequest,
      isGreetingPending,
      loadHistory,
      isHistoryLoading,
      hasHistoryLoaded,
    }),
    [
      stream, submitChat, requestChatFocus, consumeChatFocusRequest,
      isGreetingPending, loadHistory, isHistoryLoading, hasHistoryLoaded,
    ],
  );

  return <BestieChatStateContext.Provider value={value}>{children}</BestieChatStateContext.Provider>;
};

/** Access the MC shared chat stream + submit handler. Returns undefined outside the candidate shell. */
export const useBestieChatStateOptional = () => useContext(BestieChatStateContext);

export const useBestieChatState = () => {
  const ctx = useContext(BestieChatStateContext);
  if (!ctx) throw new Error('useBestieChatState must be used within BestieChatStateProvider');
  return ctx;
};
